import React, { useState, useEffect } from 'react';
import { Database, Info, ShieldCheck, Cookie } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';

export const CookiePage = () => {
  const { brand, colorMode, activeBrandId } = useTenant();
  const theme = activeBrandId;
  const isDark = colorMode === 'dark';
  const brandName = brand?.name || 'Our Platform';

  const proseClasses = cn(
    "prose prose-lg max-w-none leading-relaxed",
    "prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white"
  );

  const [activeSection, setActiveSection] = useState('what-are-cookies');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['what-are-cookies', 'how-we-use', 'your-choices'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'what-are-cookies', label: '1. What are Cookies?' },
    { id: 'how-we-use', label: '2. How We Use Cookies' },
    { id: 'your-choices', label: '3. Your Choices' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="Cookie Policy" description={`Read the cookie policy regarding data collection and usage on ${brandName}.`} />
        <div className="max-w-7xl mx-auto">
          
          <LegalHero 
            title="Cookie Policy"
            subtitle={`How we use browser cookies to keep you logged in and ensure ${brandName} works smoothly.`}
            icon={<Cookie size={14} />}
            label="Browser Data"
            version="1.1"
            updated="May 2026"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            {/* Sticky Navigation */}
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className={cn("font-bold uppercase text-[10px] tracking-widest font-mono", isDark ? "text-slate-500" : "text-slate-400")}>
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="what-are-cookies" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <Info size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. What are Cookies?</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Cookies are tiny files we place on your browser so our website can remember you. Without them, you'd have to log in every single time you click a link or open a new page.
                  </p>
                </div>
              </section>

              <section id="how-we-use" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <Database size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. How We Use Cookies</h2>
                </div>
                <div className={proseClasses}>
                  <p className="mb-4">
                    We use cookies for a few simple reasons:
                  </p>
                  <ul className={cn("list-none space-y-4 pl-0 p-6 rounded-xl border mt-6", isDark ? "bg-[#111] border-white/10" : "bg-white border-gray-100 shadow-sm")}>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className={isDark ? "text-white" : "text-slate-900"}>Essential Cookies:</strong> These keep you logged in securely so you don't get booted out while you're working.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className={isDark ? "text-white" : "text-slate-900"}>Preference Cookies:</strong> These remember if you like dark mode or light mode, so we don't blind you when you open the app at night.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-primary flex-shrink-0" />
                      <span><strong className={isDark ? "text-white" : "text-slate-900"}>Analytics Cookies:</strong> These help us see which pages people use the most so we know where to focus our updates to make the app better.</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section id="your-choices" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <ShieldCheck size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. Your Choices</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    You can control or block cookies through your browser settings. However, fair warning: if you block our "Essential" cookies, the app won't work and you won't be able to log in.
                  </p>
                  <p className="mt-4">
                    For non-essential cookies (like Analytics), you have the right to withdraw your consent at any time. You can opt-out of analytics cookies without breaking the app by visiting your Account Settings and updating your Privacy Preferences.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
